import { DAY_KEYS, type WeeklyScheduleEntry } from "@upsquad/shared";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Switch } from "@/src/common/components/ui";
import { formatTime12h } from "@/src/common/lib/date.utils";

const SLOT_MINUTES = 15;
const SLOTS_PER_HOUR = 60 / SLOT_MINUTES;
const SLOTS_PER_DAY = 24 * SLOTS_PER_HOUR;

const TIME_OPTIONS = Array.from({ length: SLOTS_PER_DAY }, (_, i) => {
	const hours = Math.floor(i / SLOTS_PER_HOUR);
	const minutes = (i % SLOTS_PER_HOUR) * SLOT_MINUTES;
	const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	return { value, label: formatTime12h(value) };
});

interface WeeklyScheduleTabProps {
	schedule: WeeklyScheduleEntry[];
	onChange: (schedule: WeeklyScheduleEntry[]) => void;
}

export function WeeklyScheduleTab({ schedule, onChange }: WeeklyScheduleTabProps) {
	const { t } = useTranslation("member");

	const getEntry = (day: number) => schedule.find((e) => e.day_of_week === day);

	const toggleDay = (day: number, enabled: boolean) => {
		if (enabled) {
			if (!getEntry(day)) {
				onChange([...schedule, { day_of_week: day, slots: [{ start_time: "08:00", end_time: "17:00" }] }]);
			}
		} else {
			onChange(schedule.filter((e) => e.day_of_week !== day));
		}
	};

	const updateSlot = (day: number, slotIndex: number, field: "start_time" | "end_time", value: string) => {
		onChange(
			schedule.map((entry) => {
				if (entry.day_of_week !== day) return entry;
				const newSlots = entry.slots.map((slot, i) => (i === slotIndex ? { ...slot, [field]: value } : slot));
				return { ...entry, slots: newSlots };
			}),
		);
	};

	const addSlot = (day: number) => {
		onChange(
			schedule.map((entry) => {
				if (entry.day_of_week !== day) return entry;
				return { ...entry, slots: [...entry.slots, { start_time: "08:00", end_time: "17:00" }] };
			}),
		);
	};

	const removeSlot = (day: number, slotIndex: number) => {
		onChange(
			schedule
				.map((entry) => {
					if (entry.day_of_week !== day) return entry;
					const newSlots = entry.slots.filter((_, i) => i !== slotIndex);
					return { ...entry, slots: newSlots };
				})
				.filter((entry) => entry.slots.length > 0),
		);
	};

	return (
		<div className="space-y-4">
			{[1, 2, 3, 4, 5, 6, 0].map((day) => {
				const entry = getEntry(day);
				const isEnabled = !!entry;
				const dayKey = DAY_KEYS[day] ?? "sunday";

				return (
					<div key={day} className="rounded-lg border border-divider p-3">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium">{t(`availability.days.${dayKey}`)}</span>
							<Switch isSelected={isEnabled} onChange={(selected) => toggleDay(day, selected)} size="sm">
								<Switch.Control>
									<Switch.Thumb />
								</Switch.Control>
							</Switch>
						</div>

						{isEnabled && entry && (
							<div className="mt-3 space-y-2">
								{entry.slots.map((slot, i) => (
									<div key={`${day}-${i}`} className="flex items-center gap-2">
										<select
											value={slot.start_time}
											onChange={(e) => updateSlot(day, i, "start_time", e.target.value)}
											className="rounded-lg border border-divider bg-transparent px-2 py-1.5 text-sm"
										>
											{TIME_OPTIONS.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										<span className="text-sm text-foreground/50">–</span>
										<select
											value={slot.end_time}
											onChange={(e) => updateSlot(day, i, "end_time", e.target.value)}
											className="rounded-lg border border-divider bg-transparent px-2 py-1.5 text-sm"
										>
											{TIME_OPTIONS.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										<button
											type="button"
											aria-label={t("availability.remove_slot")}
											onClick={() => removeSlot(day, i)}
											className="text-danger hover:text-danger/80"
										>
											<Trash2 size={14} />
										</button>
									</div>
								))}
								<Button size="sm" variant="ghost" onPress={() => addSlot(day)}>
									<Plus size={14} />
									{t("availability.add_slot")}
								</Button>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
